import React, { ReactNode, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { useTranslation } from "react-i18next"

import { SearchableDropdown, SearchableDropdownOption } from "components/inputs/SearchableDropdown"

import { getAllTimezones, getUtcOffset, UTC_TIMEZONE } from "util/date"

import { FIELD_TOOLTIPS, SectionPreset } from "../../helpers/bonus2.helpers"

import { FreeSpinGamesPicker } from "./FreeSpinGamesPicker"

// Shared control classes (kept here so every section + primitive uses the same styling).
export const inputClassName = "w-full input input-bordered bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)]"
export const selectClassName =
  "w-full select select-bordered bg-[rgba(0,0,0,0.05)] dark:bg-[rgba(0,0,0,0.2)] [&>option]:bg-base-100 [&>option]:dark:bg-[#1a1a2e]"
export const labelClassName = "label-text dark:text-gray-300"
// A cell holding a button rather than a control, on a row where the other cells hold one.
// daisyUI sizes `.input`/`.select` at 3rem, so this is what lines a button up with them.
export const controlLineClassName = "flex h-12 items-center"

// Contextual help / describe strip shown at the top of a section. `tone` colours the strip.
export function DescribeStrip({ tone = "info", children }: { tone?: "info" | "warn" | "ok"; children: ReactNode }) {
  const tones = {
    info: "border-[rgba(132,0,168,0.35)] bg-[rgba(132,0,168,0.08)] text-gray-300",
    warn: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
  }
  return <div className={`rounded-lg border px-3 py-2 text-xs leading-relaxed ${tones[tone]}`}>{children}</div>
}

// Hover tooltip. The bubble is PORTALED to document.body with position:fixed, so it escapes the
// modal's overflow-hidden / scroll clipping and is never cut off. The anchor defaults to a "?"
// icon; pass `children` to use a custom anchor (e.g. a warning dot). `border` colours the bubble.
export function Tooltip({ text, children, border = "rgba(132,0,168,0.5)" }: { text?: string; children?: ReactNode; border?: string }) {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  if (!text) return null

  const show = () => {
    const el = anchorRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setPos({ top: r.top - 8, left: r.left + r.width / 2 })
  }
  const hide = () => setPos(null)

  return (
    <>
      <span ref={anchorRef} onMouseEnter={show} onMouseLeave={hide} className="inline-flex">
        {children ?? (
          <span className="ml-1 inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-[rgba(169,78,206,0.6)] bg-[rgba(132,0,168,0.2)] text-[10px] font-bold text-[rgba(210,170,235,1)]">
            ?
          </span>
        )}
      </span>
      {pos &&
        createPortal(
          <span
            style={{ position: "fixed", top: pos.top, left: pos.left, transform: "translate(-50%, -100%)", borderColor: border }}
            className="pointer-events-none z-[9999] block w-64 max-w-[80vw] rounded-lg border bg-[#1a1a2e] px-3 py-2 text-xs leading-relaxed text-gray-200 shadow-xl"
          >
            {text}
          </span>,
          document.body
        )}
    </>
  )
}

// A labelled field cell with an optional tooltip (?), an inline description, and a non-blocking
// range warning. `full` spans both grid columns. `field` keys the tooltip; `desc`/`warn` render below.
export function Field({
  label,
  field,
  desc,
  warn,
  full = false,
  children
}: {
  label: string
  field?: string
  desc?: string
  warn?: string | null
  full?: boolean
  children: ReactNode
}) {
  const { t } = useTranslation("translation", { useSuspense: false })
  const tipKey = field ? FIELD_TOOLTIPS[field] : undefined
  const tip = tipKey ? (t(tipKey) as string) : undefined
  return (
    <div className={`form-control ${full ? "md:col-span-2" : ""}`}>
      <label className="label py-1">
        <span className={`${labelClassName} flex items-center`}>
          {label}
          <Tooltip text={tip} />
        </span>
      </label>
      {children}
      {warn && <span className="mt-1 text-xs text-amber-300">⚠ {warn}</span>}
      {desc && <span className="mt-1 text-xs leading-snug text-gray-400">{desc}</span>}
    </div>
  )
}

// Right-rail preset cards for a section. Each card shows name/description/values + an [Apply] button.
export function PresetRail({ presets, onApply }: { presets: SectionPreset[]; onApply: (p: SectionPreset) => void }) {
  const { t } = useTranslation("translation", { useSuspense: false })
  if (presets.length === 0) return null
  return (
    <div className="space-y-2">
      <div className="text-xs font-bold uppercase tracking-wide text-gray-400">{t("bonus2.form.quickPresets")}</div>
      {presets.map((p) => (
        <div key={p.id} className="rounded-lg border border-[rgba(132,0,168,0.3)] bg-[rgba(132,0,168,0.05)] p-3">
          <div className="text-sm font-semibold dark:text-white">{p.title}</div>
          <div className="mt-0.5 text-xs leading-snug text-gray-400">{p.description}</div>
          <ul className="mt-2 space-y-0.5">
            {p.summary.map((s) => (
              <li key={s} className="text-xs text-gray-300">
                • {s}
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => onApply(p)} className="btn btn-primary btn-xs mt-2 w-full">
            {t("bonus2.form.apply")}
          </button>
        </div>
      ))}
    </div>
  )
}

// Built on FIRST USE, not at module load, then cached for the life of the page. Resolving an
// offset means constructing an Intl.DateTimeFormat per zone, and ~420 of them measure ~60ms of
// synchronous main-thread work — App.tsx imports the Bonus2 view eagerly, so paying that at
// module load would tax every admin-panel boot for a field most operators never open.
let timezoneOptionsCache: SearchableDropdownOption[] | null = null

const timezoneOptions = (): SearchableDropdownOption[] => {
  if (!timezoneOptionsCache) {
    timezoneOptionsCache = getAllTimezones().map((tz) => ({
      value: tz,
      label: tz.replace(/_/g, " "),
      extra: getUtcOffset(tz)
    }))
  }
  return timezoneOptionsCache
}

export function TimezoneSelect({ value, onChange }: { value?: string; onChange: (tz: string) => void }) {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [search, setSearch] = useState("")

  const options = useMemo(() => {
    const all = timezoneOptions()
    if (!value || all.some((o) => o.value === value)) return all
    return [{ value, label: `${value} (unrecognised)`, extra: "" }, ...all]
  }, [value])

  return (
    <SearchableDropdown
      single
      mode="preloaded"
      options={options}
      selected={[value || UTC_TIMEZONE]}
      onSelect={(ids) => onChange(ids[0] ?? UTC_TIMEZONE)}
      loading={false}
      searchValue={search}
      onSearchChange={setSearch}
      placeholder={t("bonus2.form.searchTimezone") as string}
      noResultsLabel={t("bonus2.form.noTimezoneMatches") as string}
    />
  )
}

// Collapsible wrapper around the (heavy, API-fetching) game picker. Collapsed by default and LAZY:
// FreeSpinGamesPicker (and its fetches) are not mounted until "Manage" is clicked.
export function CollapsibleGamePicker({
  label,
  selectedGameIds,
  onGameIdsChange,
  tenant,
  ladderProducersOnly = false,
  singleSelect = false
}: {
  label: string
  selectedGameIds: string[]
  onGameIdsChange: (ids: string[]) => void
  tenant: string
  /** SOFT-736 — free-spins mode: only producers a stake can resolve on (see FreeSpinGamesPicker). */
  ladderProducersOnly?: boolean
  /** One game at most — see FreeSpinGamesPicker. */
  singleSelect?: boolean
}) {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-lg border border-[rgba(132,0,168,0.25)]">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-sm dark:text-gray-300">
          {label} <span className="text-gray-400">({t("bonus2.form.nSelected", { count: selectedGameIds.length })})</span>
        </span>
        <div className="flex items-center gap-2">
          {selectedGameIds.length > 0 && (
            <button type="button" onClick={() => onGameIdsChange([])} className="text-xs text-rose-400 hover:text-rose-300">
              {t("bonus2.config.clear")}
            </button>
          )}
          <button type="button" onClick={() => setOpen((v) => !v)} className="btn btn-xs btn-ghost">
            {open ? t("bonus2.form.hide") : t("bonus2.form.manage")}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-[rgba(132,0,168,0.2)] p-3">
          <FreeSpinGamesPicker
            selectedGameIds={selectedGameIds}
            onGameIdsChange={onGameIdsChange}
            tenant={tenant}
            ladderProducersOnly={ladderProducersOnly}
            singleSelect={singleSelect}
          />
        </div>
      )}
    </div>
  )
}
